/** Staff: Push Notifications — enable this device, then choose which actions send a push. One page for every alert. */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Loader2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { wget, wput } from '@/components/website/api';
import { PushToggle } from '@/components/PushToggle';

function ClockRules({ rules, onSaved }) {
  const [r, setR] = useState(rules);
  const [busy, setBusy] = useState(false);
  useEffect(() => setR(rules), [rules]);
  const save = async () => { setBusy(true); try { await wput('/push/clock-rules', { work_start: r.work_start, grace_minutes: Number(r.grace_minutes) }); toast.success('Work start time saved'); onSaved(); } catch (e) { toast.error(e.message); } finally { setBusy(false); } };
  return (
    <div className="flex flex-wrap items-end gap-2 bg-gray-50 rounded-lg p-3 mt-2" data-testid="clock-rules">
      <label className="text-xs text-gray-600">Work starts<Input type="time" value={r.work_start} onChange={(e) => setR({ ...r, work_start: e.target.value })} className="h-8 mt-1 w-32" data-testid="clock-rules-start" /></label>
      <label className="text-xs text-gray-600">Grace (min)<Input type="number" value={r.grace_minutes} onChange={(e) => setR({ ...r, grace_minutes: e.target.value })} className="h-8 mt-1 w-24" data-testid="clock-rules-grace" /></label>
      <Button size="sm" onClick={save} disabled={busy} className="h-8 bg-gray-900 text-white" data-testid="clock-rules-save">Save</Button>
      <span className="text-[11px] text-gray-500">"Late" = first clock-in after {r.work_start} + {r.grace_minutes} min.</span>
    </div>
  );
}

export default function StaffPushNotifications() {
  const navigate = useNavigate();
  const { user, isStaff } = useAuth();
  const [d, setD] = useState(null);
  const load = () => wget('/push/prefs').then(setD).catch((e) => toast.error(e.message));
  useEffect(() => { if (!isStaff) navigate('/'); else load(); }, [isStaff]); // eslint-disable-line react-hooks/exhaustive-deps
  const groups = useMemo(() => { const g = {}; (d?.events || []).forEach((e) => { (g[e.group] = g[e.group] || []).push(e); }); return g; }, [d]);
  const toggle = async (key, on) => {
    const next = Object.fromEntries((d.events || []).map((e) => [e.key, e.key === key ? on : e.on]));
    setD({ ...d, events: d.events.map((e) => (e.key === key ? { ...e, on } : e)) });
    try { setD(await wput('/push/prefs', { prefs: next })); } catch (e) { toast.error(e.message); load(); }
  };
  const setAll = async (on) => { try { setD(await wput('/push/prefs', { prefs: Object.fromEntries(d.events.map((e) => [e.key, on])) })); } catch (e) { toast.error(e.message); } };
  if (!isStaff) return null;
  return (
    <div className="min-h-screen bg-gray-50 pb-16" data-testid="staff-push-notifications">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <button onClick={() => navigate('/staff')} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-3" data-testid="push-back"><ArrowLeft className="w-4 h-4" /> Staff Dashboard</button>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Bell className="w-6 h-6 text-amber-600" /> Push Notifications</h1>
        <p className="text-sm text-gray-500 mb-5">Choose what pings your phone or desktop. These are your personal settings — every admin and manager picks their own.</p>

        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4" data-testid="push-device-card">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><Smartphone className="w-5 h-5" /></div>
            <div className="flex-1 min-w-[12rem]"><div className="font-bold text-sm">This device</div><div className="text-xs text-gray-500">Enable once per phone or computer. On iPhone, add the portal to your Home Screen first (Share → Add to Home Screen).</div></div>
            <PushToggle />
          </div>
        </div>

        {!d && <Loader2 className="w-6 h-6 animate-spin text-gray-400" />}
        {d && !d.can_receive && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="push-role-note">Push alerts go to admins and managers. Your account can still receive in-app notifications.</p>}
        {d && (
          <div className="bg-white rounded-xl border border-gray-200 divide-y" data-testid="push-events">
            <div className="p-3 flex items-center justify-between text-xs text-gray-500"><span>{d.events.filter((e) => e.on).length} of {d.events.length} alerts on</span><span className="flex gap-3"><button onClick={() => setAll(true)} className="underline" data-testid="push-all-on">All on</button><button onClick={() => setAll(false)} className="underline" data-testid="push-all-off">All off</button></span></div>
            {Object.entries(groups).map(([group, events]) => (
              <div key={group} className="p-4" data-testid={`push-group-${group.toLowerCase()}`}>
                <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-2">{group}</div>
                <div className="space-y-3">
                  {events.map((e) => (
                    <div key={e.key} className="flex items-start gap-3">
                      <Switch checked={e.on} onCheckedChange={(v) => toggle(e.key, v)} data-testid={`push-event-${e.key}`} />
                      <div className="flex-1"><div className="text-sm font-semibold text-gray-900">{e.label}</div><div className="text-xs text-gray-500">{e.desc}</div>
                        {e.key === 'clock_late' && user?.role === 'admin' && <ClockRules rules={d.clock_rules} onSaved={load} />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
